import { Link, useParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SectionCard from '@/components/feature/SectionCard';
import NewsRow from '@/components/feature/NewsRow';
import TeamBadge from '@/components/base/TeamBadge';
import { useSEO } from '@/hooks/useSEO';
import { allNews, getNews, getTeam } from '@/utils/data';

export default function NewsDetailPage() {
  const { newsId } = useParams();
  const news = getNews(newsId);

  useSEO({
    title: news ? `${news.title} - 篮球比分` : '新闻不存在 - 篮球比分',
    description: news ? news.summary : '未找到对应的新闻内容。',
    keywords: news ? `${news.category},篮球新闻,${news.title}` : '篮球新闻',
  });

  if (!news) {
    return (
      <div className="px-4 md:px-6 py-20 flex flex-col items-center text-center">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-article-line text-3xl"></i>
        </span>
        <h1 className="font-heading font-bold text-xl text-foreground-900">新闻不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-500 max-w-md">您访问的新闻可能已被移除，返回列表查看更多资讯。</p>
        <Link to="/news" className="mt-6 h-10 px-5 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold flex items-center cursor-pointer whitespace-nowrap">
          返回新闻列表
        </Link>
      </div>
    );
  }

  const related = allNews.filter((n) => n.id !== news.id && n.category === news.category).slice(0, 4);
  const relatedNews = related.length > 0 ? related : allNews.filter((n) => n.id !== news.id).slice(0, 4);
  const relatedTeams = news.relatedTeamIds.map((id) => getTeam(id)).filter(Boolean);

  return (
    <div className="px-4 md:px-6 py-6 animate-fade-up">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Breadcrumb
          items={[
            { label: '首页', to: '/' },
            { label: '新闻', to: '/news' },
            { label: news.title },
          ]}
        />
        <Link to="/news" className="flex items-center gap-1 h-9 px-3 rounded-md border border-background-200 text-xs text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap">
          <i className="ri-arrow-left-line"></i>
          返回新闻列表
        </Link>
      </div>

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <article className="lg:col-span-2">
          <div className="rounded-lg border border-background-200/80 bg-background-50 overflow-hidden">
            <div className="h-56 md:h-72 w-full overflow-hidden bg-background-200">
              <img src={news.cover} alt={news.title} className="w-full h-full object-cover object-top" />
            </div>
            <div className="p-5 md:p-8">
              <div className="flex items-center gap-3 flex-wrap text-xs md:text-sm text-foreground-500">
                <span className="px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800">{news.category}</span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-user-line"></i></span>
                  {news.author}
                </span>
                <span className="flex items-center gap-1">
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-calendar-line"></i></span>
                  {news.date}
                </span>
              </div>

              <h1 className="mt-4 font-heading font-extrabold text-2xl md:text-3xl text-foreground-950 leading-snug">{news.title}</h1>
              <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed border-l-4 border-primary-300 pl-4 bg-background-100/60 py-3 rounded-r-md">
                {news.summary}
              </p>

              <div className="mt-6 space-y-4">
                {news.content.map((para, i) => (
                  <p key={i} className="text-sm md:text-[15px] text-foreground-700 leading-7 md:leading-8">{para}</p>
                ))}
              </div>

              {relatedTeams.length > 0 && (
                <div className="mt-8 pt-6 border-t border-background-200/70">
                  <h2 className="font-heading font-bold text-base text-foreground-900 mb-3">相关球队</h2>
                  <div className="flex items-center gap-3 flex-wrap">
                    {relatedTeams.map((t) => t && (
                      <Link
                        key={t.id}
                        to={`/team/${t.id}`}
                        className="flex items-center gap-2 px-3 py-2 rounded-md border border-background-200/80 hover:border-primary-300 hover:bg-primary-50/50 transition-colors cursor-pointer"
                      >
                        <TeamBadge team={t} size="sm" />
                        <span className="text-sm text-foreground-800">{t.name}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {news.relatedMatchId && (
                <div className="mt-4">
                  <Link
                    to={`/detail/${news.relatedMatchId}`}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-md bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-basketball-line"></i>
                    查看相关比赛详情
                  </Link>
                </div>
              )}
            </div>
          </div>
        </article>

        <aside className="space-y-6">
          <SectionCard title="相关文章" icon="ri-newspaper-line">
            <div>
              {relatedNews.map((n) => (
                <NewsRow key={n.id} news={n} />
              ))}
            </div>
          </SectionCard>
        </aside>
      </div>
    </div>
  );
}